<!-- src/components/device/commands/PairingCard.vue -->
<!--
  Jumelage d'un accessoire radio — partagé entre l'espace admin et l'espace client.

  Le client avait un bouton qui publiait bien `pair_start` mais n'affichait RIEN ensuite : ni
  décompte, ni résultat, ni refus motivé de la carte. Il appuyait, ne voyait rien, et concluait
  que le bouton ne marchait pas. Le décompte et l'écoute des résultats (`device:accessories`,
  `device:pair_refused`) vivent donc ici, et non plus dans la seule vue admin.

  `send(command, data)` est fourni par la vue parente et renvoie une promesse de booléen :
  le décompte ne démarre que si le serveur a accepté la commande.
-->
<template>
  <div class="bg-white rounded-lg shadow p-4">
    <h3 class="font-semibold text-gray-800">
      {{ $t('jumelage.titre') }}
      <span v-if="technique" class="ml-1 text-xs font-normal text-gray-400">pair_start</span>
    </h3>
    <p class="mt-1 text-sm text-gray-500">
      {{ $t('jumelage.intro') }}
    </p>

    <div class="mt-3 space-y-2">
      <div v-if="active" class="flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 border border-blue-200">
        <div class="flex items-center gap-2">
          <span class="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
          <span class="text-sm font-medium text-blue-700">{{ $t('jumelage.enCours') }}</span>
        </div>
        <span class="text-2xl font-bold text-blue-700 tabular-nums">{{ countdown }}s</span>
      </div>
      <div v-else class="flex flex-wrap gap-2">
        <button @click="demarrer" class="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed" :disabled="busy || injoignable">
          {{ $t('jumelage.demarrer') }}
        </button>
        <!-- Actions voisines propres à une vue (le client y garde « Retirer tous les accessoires »). -->
        <slot name="actions" />
      </div>

      <!-- Carte endormie hors fenêtre de maintenance : le jumelage ne passerait pas (action
           immédiate, refusée aussi par le serveur). Il se fait pendant la fenêtre. -->
      <p v-if="injoignable && !active" class="text-xs text-amber-700">
        {{ $t('jumelage.endormie') }}
      </p>

      <div v-if="result === 'success'" class="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
        <span>✓</span> {{ $t('jumelage.reussi') }}
      </div>
      <div v-if="result === 'failed'" class="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
        <span>✕</span> {{ $t('jumelage.echoue') }}
      </div>
      <!-- Refus MOTIVÉ par la carte : à distinguer du cas ci-dessus. « Échec » veut dire que
           la fenêtre s'est écoulée sans qu'aucune télécommande soit pressée ; « refusé »
           veut dire que la fenêtre ne s'est jamais ouverte, et la carte dit pourquoi. -->
      <div v-if="result === 'refused'" class="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
        <span class="font-medium">✕</span>
        <span>{{ refusedMessage || $t('jumelage.refuse') }}</span>
      </div>
      <!-- Refus TRANSITOIRE (`busy`) : ni rouge ni « échec ». La carte était occupée une
           quinzaine de secondes ; le geste à faire est de relancer, pas de renoncer. -->
      <div v-if="result === 'busy'" class="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
        <div class="flex items-start gap-2">
          <span class="font-medium">⏳</span>
          <span>{{ refusedMessage || $t('jumelage.occupee') }}</span>
        </div>
        <button @click="demarrer" class="btn btn-primary text-sm mt-2" :disabled="busy">
          {{ $t('jumelage.relancer') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onBeforeUnmount } from 'vue';
import socketService from '@/services/socket.service';
import { texteEvenement } from '@/i18n/serveur';

// Raisons de refus connues du catalogue partagé (serveur.valeurs.raison) ; toute autre est « inconnue ».
const RAISONS_CONNUES = ['rf_fault', 'rf_disabled', 'busy'];

export default defineComponent({
  name: 'PairingCard',

  props: {
    deviceId:  { type: Number, required: true },
    send:      { type: Function, required: true },
    busy:      { type: Boolean, default: false },
    technique: { type: Boolean, default: false },
    // Carte endormie hors fenêtre de maintenance (utils/carte.js).
    injoignable: { type: Boolean, default: false }
  },

  setup(props) {
    const active = ref(false);
    const countdown = ref(0);
    const result = ref(null); // null | 'success' | 'failed' | 'refused' | 'busy'
    const refusedMessage = ref(null);
    let timer = null;
    let resultTimer = null;

    const finir = (r) => {
      active.value = false;
      clearInterval(timer);
      timer = null;
      result.value = r;
      if (resultTimer) clearTimeout(resultTimer);
      // Un refus porte une consigne à lire et à appliquer (réactiver la radio, patienter, faire
      // remonter la carte) : 4 s suffisent pour un « ✓ jumelé », pas pour deux phrases d'explication.
      const holdMs = (r === 'refused' || r === 'busy') ? 15000 : 4000;
      resultTimer = setTimeout(() => { result.value = null; }, holdMs);
    };

    const lancerDecompte = () => {
      active.value = true;
      result.value = null;
      refusedMessage.value = null;
      countdown.value = 15;
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) finir('failed');
      }, 1000);
    };

    const demarrer = async () => {
      if (await props.send('pair_start')) lancerDecompte();
    };

    // Handlers NOMMÉS : la vue parente écoute les mêmes événements (rechargement de la liste
    // d'accessoires) ; un `off` sans handler retirerait aussi les siens au changement d'onglet.
    const socket = socketService.connect();
    const onAccessories = (data) => {
      if (data.deviceId !== props.deviceId) return;
      // Seul un AJOUT d'accessoire est un jumelage réussi : un retrait, une alerte de pile ou une
      // liste rafraîchie pendant le décompte passaient aussi pour un succès (relevé du 02/10/2026).
      if (active.value && data.action === 'pair') finir('success');
    };
    // Refus de jumelage motivé (fw >= 66356). Sans lui, le décompte allait au bout de ses 15 s
    // et annonçait « échec » sans distinguer une télécommande non pressée d'une radio hors
    // service. `busy` est TRANSITOIRE (~15 s) : l'annoncer comme un échec ferait renoncer.
    const onRefused = (data) => {
      if (data.deviceId !== props.deviceId) return;
      // En français : le texte du serveur tel quel ; ailleurs, traduit d'après la raison.
      refusedMessage.value = texteEvenement({
        message: data.message,
        code: 'JUMELAGE_REFUSE',
        params: { raison: RAISONS_CONNUES.includes(data.reason) ? data.reason : 'inconnue' }
      }) || null;
      finir(data.transient ? 'busy' : 'refused');
    };
    socket.on('device:accessories', onAccessories);
    socket.on('device:pair_refused', onRefused);

    onBeforeUnmount(() => {
      socket.off('device:accessories', onAccessories);
      socket.off('device:pair_refused', onRefused);
      if (timer) clearInterval(timer);
      if (resultTimer) clearTimeout(resultTimer);
    });

    return { active, countdown, result, refusedMessage, demarrer };
  }
});
</script>
